import {
  Box,
  CheckboxInput,
  Flex,
  Heading,
  mdBumps,
  ModalHeader,
  PrimaryButton,
  SecondaryButton,
  xlBumps,
  xsBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback, useState } from 'react'

import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { usePrincipal } from '../../../../authentication'
import { useProposal } from '../../../proposal/proposalContext'
import { PromoTerms } from '../PromoTerms'
import { PromoModalContentProps } from './types'

const formId = 'promotion-form'

export const useAddPromotionCallback = (
  promoCode: string,
  promotionActivationCode?: string,
  onComplete?: () => void,
) => {
  const { apiClient } = usePrincipal()
  const {
    proposal: { proposalCode },
    setProposal,
  } = useProposal()

  return useApiErrorHandlingAsyncCallback(async () => {
    setProposal(
      await apiClient.updatePromoStatus(proposalCode, promoCode, promotionActivationCode),
    )
    onComplete?.()
  })
}

const tKey = TKey('components.promotion-modal')

export const PromoNoCodeModalContent: FunctionComponent<PromoModalContentProps> = ({
  promoCode,
  promoDiscount,
  onCancel,
  onComplete,
}) => {
  const { t } = useWriskTranslation()

  const [assumptionsConfirmed, setAssumptionsConfirmed] = useState(false)
  const onAssumptionsChecked = useCallback(
    (value: boolean) => setAssumptionsConfirmed(value),
    [],
  )

  const onSubmit = useAddPromotionCallback(promoCode, promoCode, onComplete)

  return (
    <Box>
      <ModalHeader
        header={t(tKey(promoCode, 'header'))}
        subheader={t(tKey(promoCode, 'subheader'), {
          amount: promoDiscount,
          defaultValue: null,
        })}
      />

      <Heading typoSize='md' py={xsBumps} mt={mdBumps}>
        {t(tKey(promoCode, 'terms.header'))}
      </Heading>

      <PromoTerms promoCode={promoCode} />

      <Flex width={1} variant='raised' flexDirection='column' py={2} mt={mdBumps}>
        <CheckboxInput
          name='assumptions'
          id='assumptions'
          variant='inset'
          value={assumptionsConfirmed}
          onChange={onAssumptionsChecked}
        >
          {t(tKey('assumptions.checkbox'))}
        </CheckboxInput>
      </Flex>

      <Flex width={1} mt={xlBumps}>
        <PrimaryButton
          width={1}
          form={formId}
          loading={onSubmit.loading}
          disabled={!assumptionsConfirmed}
          onClick={onSubmit.execute}
          className='page-actions__action'
        >
          {t(tKey('actions.confirm'))}
        </PrimaryButton>
        <SecondaryButton width={1} ml={xsBumps} onClick={onCancel}>
          {t(tKey('actions.cancel'))}
        </SecondaryButton>
      </Flex>
    </Box>
  )
}
